import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getUnits,
  getUnit,
  filterUnits,
  getDoorPin,
  removeUnit,
  setUnitPagination
} from '../../../../redux/actions/unit';
import { CDropdown, CDropdownToggle, CPagination } from '@coreui/react';
import { changeOpenedModal } from '../../../../redux/actions/headerModal';
import { setToast } from '../../../../redux/actions/window';
import { getFullDate12Hr } from '../../../../helper';
import { ReactComponent as FullBattery } from '../../../../assets/icons/battery/full.svg';
import { ReactComponent as SeventyPercent } from '../../../../assets/icons/battery/seventyPercent.svg';
import { ReactComponent as FortyPercent } from '../../../../assets/icons/battery/fortyPercent.svg';
import { ReactComponent as TwentyPercent } from '../../../../assets/icons/battery/twentyPercent.svg';
import { ReactComponent as NotActive } from '../../../../assets/icons/battery/notActive.svg';
import DeleteModal from '../../../../components/DeleteModal';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { setCsvFields } from '../../../../redux/actions/reports';
import { DATE_FORMATS, UserRole } from '../../../users/constant';
import { LOADING_BLUE } from '../../../../utils/colorCode';
import { setGlobalBuildingStatus, setGlobalBuilding } from '../../../../redux/actions/building';
import { requestPending } from '../../../../utils/status';
import { SEARCH, SET_GLOBAL_BUILDING } from '../../../../redux/constants';
import Table from '../../../../components/Table';
import { StyledDropdown, StyledDropdownItem } from '../../../../components/GlobalTableEditDropdown';
import { FaInfoCircle, FaTrash } from 'react-icons/fa';
import { Separator } from '../../../../components/ui/Separator';
import { RiPencilFill } from 'react-icons/ri';
import { AVAILABLE_TABS } from '..';
import moment from 'moment';
import { useGetConfigs } from '../../../../hooks/useGetConfigs';
import styled from 'styled-components';
import { openModal } from '../../../../helper/modals';
import RentStatusChip from '../../../../components/RentStatusChip';
import { waterLeakTempFixOrgIds, waterLeakTempFixUnitIds } from '../../constants';
import { shouldHideBatteryLevel, supportsPinHistory } from '../../../../utils/lockInfo';

const Unit = ({ useSearchMode, onPageChange, onRefetch, onSearchSortChange }) => {
  const history = useHistory();
  const { building, buildingsPerRole } = useSelector((state) => state.building);
  const { globalOrgId } = useSelector((state) => state.Organization);

  const buildingIdFromUrl = useMemo(() => {
    const raw = history.location.pathname.split('/')[2];
    const n = parseInt(raw, 10);
    return Number.isNaN(n) ? 0 : n;
  }, [history.location.pathname]);

  const buildingHasVopayId =
    building?.vopay_account_id != null && building?.vopay_account_id !== '';
  const { units, status, pagination } = useSelector((state) => state.unit);

  const searchState = useSelector((state) => state.search);

  const displayUnits = useSearchMode ? (searchState.currentData ?? []) : units;
  const displayPagination = useSearchMode ? (searchState.currentPaginationData ?? {}) : pagination;
  const isLoading = useSearchMode
    ? (searchState.searchStatus === requestPending(SEARCH))
    : (status === 'GET_UNITS_PENDING');
  const { config } = useGetConfigs();
  const [showModal, setShowModal] = useState({
    id: null,
    show: false
  });
  const [activePage, setActivePage] = useState(1);
  const [numOfPages, setNumOfPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [sort, setSort] = useState('unit_num');
  const [sortOrder, setSortOrder] = useState('asc');
  const [sorterValue, setSorterValue] = useState({ column: 'unit_num', asc: true });
  const prevBuildingIdFromUrlRef = useRef(buildingIdFromUrl);

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { roleId } = useSelector((state) => state.auth);

  const isSuperAdmin = parseInt(roleId) === UserRole.SUPER_ADMIN;

  const canViewLockInfo =
    config?.show_user_pin_admin || config?.show_user_pin_to_admin || isSuperAdmin;

  // PMs can see PIN history when the building's show-PIN config lets them see lock info
  const canViewPinHistory = canViewLockInfo && supportsPinHistory(config?.lock_provider);
  const hideBatteryLevel = shouldHideBatteryLevel(building);

  useEffect(() => {
    const commonFields = [
      {
        key: 'unit_num',
        label: config?.suite_enabled ? t('Room Number') : t('Unit Number')
      },
      { key: 'floor', label: t('Floor') },
      {
        key: 'resident',
        label: t('Resident')
      },
      ...(buildingHasVopayId ? [{ key: 'rent_status', label: t('Rent Status') }] : []),
      { key: 'battery_level', label: t('Battery') }
    ];

    if (config?.suite_enabled) {
      commonFields.push({ key: 'suite_name', label: 'Unit Name' });
    }

    dispatch(
      setCsvFields(commonFields.filter((f) => !(hideBatteryLevel && f.key === 'battery_level')))
    );
  }, [dispatch, t, building, config?.suite_enabled, buildingHasVopayId]);

  useEffect(() => {
    if (useSearchMode) return;
    if (!buildingIdFromUrl) return;

    const buildingChanged = prevBuildingIdFromUrlRef.current !== buildingIdFromUrl;
    prevBuildingIdFromUrlRef.current = buildingIdFromUrl;
    const pageToFetch = buildingChanged ? 1 : activePage;
    if (buildingChanged) {
      setActivePage(1);
    }

    dispatch(
      getUnits({
        id: buildingIdFromUrl,
        page: pageToFetch,
        sort: sort,
        sortOrder: sortOrder,
        perPage: itemsPerPage
      })
    );
    dispatch(
      setUnitPagination({
        pagination: {
          ...pagination,
          sort: sort,
          sortOrder: sortOrder
        }
      })
    );
  }, [dispatch, activePage, sort, sortOrder, itemsPerPage, useSearchMode, buildingIdFromUrl]);

  const fields = useMemo(() => {
    const memoFields = [
      {
        key: 'unit_num',
        label: config?.suite_enabled ? t('Room Number') : t('Unit Number'),
        sorter: true,
        filter: false
      },
      { key: 'floor', label: t('Floor'), sorter: true, filter: false },
      { key: 'resident', label: t('Resident'), sorter: true, filter: false },
      ...(buildingHasVopayId
        ? [{ key: 'rent_status', label: t('Rent Status'), sorter: false, filter: false }]
        : []),
      { key: 'battery_level', label: t('Battery'), sorter: true, filter: false }
    ];

    if (config.enable_akuvox && config.enable_akuvox_private_pins) {
      memoFields.push({
        key: 'intercom_pins',
        label: t('Intercom pins'),
        sorter: false,
        filter: false
      });
    }

    if (canViewLockInfo) {
      memoFields.push({
        key: 'lock_info',
        label: t('Lock info'),
        sorter: false,
        filter: false
      });
    }

    memoFields.push({ key: 'edit', label: '', sorter: false, filter: false });
    return memoFields.filter((f) => !(hideBatteryLevel && f.key === 'battery_level'));
  }, [config, roleId, building, buildingHasVopayId, t]);

  const handleSortChange = (e) => {
    let nextSort = 'unit_num';
    switch (e.column) {
      case 'floor':
        nextSort = 'floor';
        break;
      case 'unit_num':
        nextSort = 'unit_num';
        break;
      case 'resident':
        nextSort = 'resident';
        break;
      case 'battery_level':
        nextSort = 'battery_level';
        break;
      default:
        nextSort = 'unit_num';
    }

    let nextSortOrder = 'asc';
    switch (e.asc) {
      case true:
        nextSortOrder = 'asc';
        break;
      case false:
        nextSortOrder = 'desc';
        break;
      default:
        nextSortOrder = 'asc';
    }

    setSort(nextSort);
    setSortOrder(nextSortOrder);
    setSorterValue({ column: nextSort, asc: nextSortOrder === 'asc' });

    const sqlOrder = nextSortOrder === 'desc' ? 'DESC' : 'ASC';
    if (useSearchMode && onSearchSortChange) {
      if (e.column === 'floor') {
        onSearchSortChange({ 'u.floor': sqlOrder });
      } else if (e.column === 'unit_num') {
        onSearchSortChange({ 'u.unit_num': sqlOrder });
      } else if (e.column === 'resident') {
        onSearchSortChange({ resident: sqlOrder });
      } else if (e.column === 'battery_level') {
        onSearchSortChange({ battery_level: sqlOrder });
      }
    }
  };

  const handleEdit = (id) => {
    dispatch(
      getUnit({
        id,
        success: () => {
          dispatch(
            changeOpenedModal({
              modal: 'editRoom'
            })
          );
        },
        fail: () => {
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('failed to get unit data!'),
              modal: 'errorToast'
            })
          );
        }
      })
    );
  };

  const handleDetails = (id) => {
    const searchDate = moment().subtract(7, 'd');
    dispatch(
      getUnit({
        id,
        day: searchDate.date(),
        month: searchDate.month(),
        year: searchDate.year(),
        success: () => {
          dispatch(
            changeOpenedModal({
              modal: 'UnitDetail',
              data: config?.suite_enabled
            })
          );
        },
        fail: () => {
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('failed to get unit data!'),
              modal: 'errorToast'
            })
          );
        }
      })
    );
  };

  const handleActivities = (id) => {
    dispatch(
      getUnit({
        id,
        success: () => {
          dispatch(
            changeOpenedModal({
              modal: 'unitStatistics'
            })
          );
        },
        fail: () => {
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('failed to get unit data!'),
              modal: 'errorToast'
            })
          );
        }
      })
    );
  };

  const handleDelete = () => {
    dispatch(
      removeUnit({
        id: showModal.id,
        success: () => {
          let page = activePage;
          if (page > 1 && units && units.length === 1) {
            page -= 1;
            setActivePage(page);
          }

          if (useSearchMode && onRefetch) {
            onRefetch();
          } else {
            dispatch(
              getUnits({
                id: building.id,
                page,
                sort: sort,
                sortOrder: sortOrder,
                perPage: itemsPerPage
              })
            );
          }
        
          const globalBuildings = buildingsPerRole?.data.map((building) => building.id) || [];
          if (globalBuildings.includes(building.id)) {
            dispatch(setGlobalBuildingStatus({ status: requestPending(SET_GLOBAL_BUILDING) }));
            dispatch(
              setGlobalBuilding({
                id: globalOrgId
              })
            );
          }
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: config?.suite_enabled
                ? t('Room has been successfully deleted!')
                : t('Unit has been successfully deleted!')
            })
          );
          setShowModal((prev) => ({
            ...prev,
            show: false
          }));
        },
        fail: () => {
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('Unit deletion failed!'),
              modal: 'errorToast'
            })
          );
        }
      })
    );
  };

  const handleGetDoorPin = (id, unitNum) => {
    id != '' &&
      dispatch(
        getDoorPin({
          id,
          data: { unit_num: unitNum, serial: id },
          success: () => {
            dispatch(
              changeOpenedModal({
                modal: 'unitLockDetails',
                data: { from: AVAILABLE_TABS.UNITS }
              })
            );
          },
          fail: () => {
            dispatch(
              setToast({
                toastShow: true,
                toastMessage: 'failed to get unit info!',
                modal: 'errorToast'
              })
            );
          }
        })
      );
  };

  const actionForPagination = (page) => {
    if (useSearchMode && onPageChange) {
      onPageChange(page);
      return;
    }
    if (page) {
      setActivePage(page);
    }
  };

  const handlePaginationChange = (e) => {
    setItemsPerPage(e);
  };

  const handleSpecificColClick = (item, index, col, e) => {
    if (col !== 'lock_info' && col !== 'edit' && col !== 'intercom_pins') {
      handleDetails(item.id);
    }
  };

  const getBatteryIcon = useCallback((batteryLevel) => {
    if (batteryLevel >= 75) {
      return <FullBattery />;
    } else if (batteryLevel >= 40) {
      return <SeventyPercent />;
    } else if (batteryLevel >= 20) {
      return <FortyPercent />;
    } else if (batteryLevel >= 0) {
      return <TwentyPercent />;
    } else {
      return <NotActive />;
    }
  }, []);

  return (
    <>
      <Table
        noItemsView={{
          noResults: t('no filtering results available'),
          noItems: t('no items available')
        }}
        // columnFilter
        sorter={{ external: true }}
        sorterValue={sorterValue}
        onSorterValueChange={handleSortChange}
        onPaginationChange={handlePaginationChange}
        items={displayUnits}
        fields={fields}
        hover
        clickableRows
        onRowClick={handleSpecificColClick}
        loading={isLoading}
        onFilteredItemsChange={(val) => dispatch(filterUnits(val))}
        scopedSlots={{
          unit_num: (item) => {
            return (
              <td
                style={{
                  fontSize: '16px'
                }}>
                <strong>{item['unit_num']}</strong>
              </td>
            );
          },
          resident: (item) => {
            const text = item.resident != null ? String(item.resident) : '';
            const isEmpty = text.trim() === '';
            return (
              <td>
                {isEmpty ? '-' : text}
              </td>
            );
          },
          ...(buildingHasVopayId
            ? {
                rent_status: (item) => (
                  <td>
                    <RentStatusChip
                      rentConfigured={item.rent_configured}
                      rentStatus={item.rent_status}
                    />
                  </td>
                )
              }
            : {}),
          create_date: (item, index) => {
            return <td>{getFullDate12Hr(item.create_date)}</td>;
          },
          lease_expire: (item, index) => {
            return <td>{item?.lease_expiry ? item?.lease_expiry : 'No active lease'}</td>;
          },
          battery_level: (item, index) => {
            const batteryLevel = item?.battery_level >= 0 ? item?.battery_level + '%' : '';
            const batteryLevelIcon = getBatteryIcon(item?.battery_level);
            return (
              <td>
                {batteryLevel} {batteryLevelIcon}
              </td>
            );
          },
          lock_info: (item) => {
            return canViewLockInfo ? (
              <ViewHyperlink
                disabled={item.lock_id === ''}
                onClick={() => handleGetDoorPin(item.lock_id, item.unit_num)}>
                {t('View')}
              </ViewHyperlink>
            ) : (
              <ViewHyperlink disabled>{t('View')}</ViewHyperlink>
            );
          },
          intercom_pins: (item) => {
            return (
              <ViewHyperlink onClick={() => openModal('ViewUnitIntercomPinsModal', item)}>
                {t('View')}
              </ViewHyperlink>
            );
          },
          edit: (item, index) => {
            const isWaterLeakTempFixUnit = waterLeakTempFixOrgIds.includes(globalOrgId) && waterLeakTempFixUnitIds.includes(item.id);
            return (
              <td className="text-right edit-width edit-container">
                <CDropdown className="tableEditDropdown">
                  <CDropdownToggle>
                    <RiPencilFill size={24} />
                  </CDropdownToggle>
                  <StyledDropdown>
                    <StyledDropdownItem onClick={() => handleDetails(item.id)}>
                      <FaInfoCircle size={16} className="mr-2" />
                      {t('Details')}
                    </StyledDropdownItem>
                    <Separator orientation="horizontal" />
                    <StyledDropdownItem onClick={() => handleActivities(item.id)}>
                      <FaInfoCircle size={16} className="mr-2" />
                      {t('Activities')}
                    </StyledDropdownItem>
                    {canViewPinHistory && (
                      <>
                        <Separator orientation="horizontal" />
                        <StyledDropdownItem
                          onClick={() =>
                            openModal('PinHistoryModal', {
                              unitId: item.id,
                              label: item.unit_num
                            })
                          }>
                          <FaInfoCircle size={16} className="mr-2" />
                          {t('PIN History')}
                        </StyledDropdownItem>
                      </>
                    )}
                    {(config?.org_allow_edits || parseInt(roleId) == UserRole.SUPER_ADMIN) && (
                      <>
                        <Separator orientation="horizontal" />
                        <StyledDropdownItem onClick={() => handleEdit(item.id)}>
                          <RiPencilFill size={16} className="mr-2" />
                          {t('Edit')}
                        </StyledDropdownItem>
                      </>
                    )}
                    {parseInt(roleId) > UserRole.VENDOR && !isWaterLeakTempFixUnit && (
                      <>
                        <Separator orientation="horizontal" />
                        <StyledDropdownItem
                          className="deleteOption"
                          onClick={() => {
                            setShowModal((prev) => ({
                              show: true,
                              id: item.id
                            }));
                            dispatch(
                              changeOpenedModal({
                                modal: 'deleteModal'
                              })
                            );
                          }}>
                          <FaTrash size={16} className="mr-2" /> {t('Delete')}
                        </StyledDropdownItem>
                      </>
                    )}
                  </StyledDropdown>
                </CDropdown>
              </td>
            );
          }
        }}
      />
      <div className={'mt-2'}>
        <CPagination
          activePage={useSearchMode ? (displayPagination?.currentPage ?? searchState.pagination?.page ?? 1) : activePage}
          pages={
            useSearchMode
              ? (displayPagination?.totalPages ?? 1)
              : (pagination?.total ? Math.ceil(pagination?.total / itemsPerPage) : numOfPages)
          }
          onActivePageChange={(i) => actionForPagination(i)}></CPagination>
      </div>

      {showModal.show && (
        <DeleteModal
          type={config?.suite_enabled ? 'Room' : 'Unit'}
          action={handleDelete}
          setShowModal={setShowModal}
          description={`Are you sure you want to delete this ${
            config?.suite_enabled ? 'room' : 'unit'
          }?`}
        />
      )}
    </>
  );
};

const ViewHyperlink = styled.td`
  font-size: 16px !important;
  cursor: pointer;
  color: ${LOADING_BLUE};
  text-decoration: underline;
  z-index: 2;

  ${({ disabled }) =>
    disabled &&
    `
    cursor: not-allowed;
    color: #AEB0B5 !important;
    text-decoration: none;
  `}
`;

export default Unit;
